"use client";
import React, {forwardRef, useState} from "react";



import {zodResolver} from '@hookform/resolvers/zod';
import {Controller, useForm} from 'react-hook-form';
import {z} from 'zod';

import {Form, FormControl, FormField, FormItem, FormMessage} from '@/components/ui/form';
import {Input} from '@/components/ui/input';
import {Textarea} from "@/components/ui/textarea";
import {Checkbox} from "@/components/ui/checkbox";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select";
import {provinces} from "@/components/FormLead";
import {useRouter} from "next/navigation";
import {getLeadSourceValue} from "@/utils/leadSource";

export interface FromNoleggioProps {
    articolo: string;
    marca: string;
    modello: string;
    title?: string;
    durata: string;
    km: string;
    anticipo: string;
    cauzione?: string;
    selectedServices?: { cprownum: number; descrizione_nclad: string; importo: string }[];
    availableServices?: { cprownum: number; codice_nclad: string; descrizione_nclad: string; importo: string; obbligatorio_nclad: number }[];
    templateMarkdown?: string;
    staticContent?: Record<string, unknown>;
    variant?: 'default' | 'modal';
    onQuoteReady?: (pdfUrl: string) => void;
}


const formSchema = z.object({
    nome: z.string().min(1, {message: 'Il nome è obbligatorio.'}),
    cognome: z.string().min(1, {message: 'Il cognome è obbligatorio.'}),
    email: z.string().email({message: 'Inserisci un indirizzo email valido.'}).min(1, {message: 'L\'email è obbligatoria.'}),
    telefono: z.string().min(10, {message: 'Il numero di telefono deve avere almeno 10 cifre.'}).min(1, {message: 'Il telefono è obbligatorio.'}),
    descrizione: z.string().optional(),
    provincia: z.string().min(1, {message: 'La provincia è obbligatoria.'}),
    terms: z.boolean().default(false).refine(val => val, {message: 'Devi accettare i termini e le condizioni.'})
});

const FromNoleggio = forwardRef<HTMLDivElement, FromNoleggioProps>(({
                                                                        articolo,
                                                                        availableServices = [], templateMarkdown = '', staticContent = {},
                                                                        marca,
                                                                        modello,
                                                                        durata,
                                                                        km,
                                                                        anticipo,
                                                                        cauzione = '0',
                                                                        title = 'Compila i dati per stampare il preventivo', variant = 'default', onQuoteReady, selectedServices = [] }, ref) => {

    const router = useRouter();
    const [submitError, setSubmitError] = useState('');

    const form = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onTouched',
        defaultValues: {
            nome: '',
            cognome: '',
            email: '',
            telefono: '',
            provincia: '',
            descrizione: '',
            terms: false
        }
    });

    const onSubmit = async (data: any) => {
        setSubmitError('');

        // Deconstruisco i dati dal form
        const {nome, email, cognome, telefono, provincia, descrizione} = data;

        // Preparo i dati da inviare nel body della richiesta
        const body = variant === 'modal' ? {
            createLead: true,
            lead: {
                nome: nome ?? '',
                cognome: cognome ?? '',
                email: email ?? '',
                telefono: telefono ?? '',
                provincia: provincia ?? '',
                motivazione: descrizione || `Preventivo Rent to Buy ${marca} ${modello} ${articolo}`,
                fonte: getLeadSourceValue(),
            },
            pdfData: {
                cliente: { nome, cognome, nominativo: `${nome} ${cognome}`.trim().toLocaleUpperCase('it-IT'), email, telefono },
                veicolo: {
                    marca, modello,
                    codiceArticolo: articolo,
                    fotoUrl: undefined,
                    alimentazione: undefined,
                    chilometraggio: undefined,
                },
            },
            configuration: {
                annualKm: Number(km),
                durationMonths: Number(durata),
                advance: Number(anticipo),
                deposit: Number(cauzione),
                category: km === '40000' ? 'SUPER1' : km,
            },
            services: availableServices.map(service => ({
                code: service.codice_nclad || String(service.cprownum),
                cprownum: service.cprownum,
                selected: selectedServices.some(selected => selected.cprownum === service.cprownum),
                name: service.descrizione_nclad,
                amount: Number(String(service.importo).replace(',', '.')),
                mandatory: service.obbligatorio_nclad === 1,
            })),
            templateMarkdown: templateMarkdown.trim() || '# Preventivo Rent to Buy\n\nCliente: {{cliente.nominativo}}\n\n{{schedule_table}}',
            staticContent,
        } : {
            origine: 'preventivo ' + articolo,
            argomento: 'LDNL',
            nome: nome ?? '',
            cognome: cognome ?? '',
            telefono: telefono ?? '',
            email: email ?? '',
            provincia: provincia ?? '',
            citta: provincia ? provinces.find((p) => p.code === provincia)?.name : '',
            motivazione: descrizione ?? marca + ' ' + modello + ' ' + articolo,
            fonte: getLeadSourceValue(),
            articolo: articolo,
            marca: marca,
            modello: modello,
            durata: durata,
            km: km,
            anticipo: anticipo,
            cauzione,
            selectedServices,
        };

        // Invio la richiesta POST all'API
        try {
        const endpoint = variant === 'modal'
            ? `${process.env.NEXT_PUBLIC_API_URL}/api/rent-to-buy/preventivo`
            : `${process.env.NEXT_PUBLIC_API_URL}/api/wsdl/lead`;
        const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
        });

        // Controllo la risposta
        const result = await response.json();


        if (response.ok && result.status === 'success') {
            if (variant === 'modal') {
                const pdfUrl = typeof result.pdf === 'string' ? result.pdf.trim() : '';
                const resolvedUrl = pdfUrl ? new URL(pdfUrl, window.location.origin) : null;
                const allowedPdfUrl = resolvedUrl && (
                    resolvedUrl.protocol === 'https:' || resolvedUrl.protocol === 'http:' ||
                    resolvedUrl.protocol === 'blob:' ||
                    (resolvedUrl.protocol === 'data:' && pdfUrl.startsWith('data:application/pdf'))
                );
                if (!allowedPdfUrl) {
                    setSubmitError('Il preventivo non è ancora disponibile per il download. Riprova più tardi.');
                    return;
                }
                form.reset();
                onQuoteReady?.(pdfUrl);
                return;
            }

            if (result.pdf) {
                // Creare un link temporaneo per il download
                const link = document.createElement('a');
                link.href = result.pdf;
                link.target = '_blank';
                link.download = 'preventivo.pdf'; // Imposta il nome del file se necessario
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }

            form.reset();
            const descrizione = title + ' ' + marca + ' ' + modello + ' ' + articolo;
            router.push(`/thank-you-page/noleggio?descrizione=${encodeURIComponent(descrizione)}&email=${encodeURIComponent(email)}&nome=${encodeURIComponent(nome)}&telefono=${encodeURIComponent(telefono)}`);

        } else {
            // Gestisci l'errore, ad esempio mostrando un messaggio di errore all'utente
            setSubmitError('Non è stato possibile scaricare il preventivo. Riprova più tardi.');
        }
        } catch {
            setSubmitError('Non è stato possibile scaricare il preventivo. Riprova più tardi.');
        }

        // Reindirizzo alla pagina thank-you con i parametri dinamici

    };

    return (
        <section ref={ref} id="form-noleggio" className={variant === 'modal' ? 'w-full' : 'my-24'}>
            {variant === 'default' && <><h5 className="text-4xl font-normal">{title}</h5><p className="text-xl font-normal mb-8">senza impegno</p></>}

            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className={variant === 'modal' ? 'space-y-2.5 sm:space-y-3' : 'space-y-6'}>
                    <div className={variant === 'modal' ? 'grid grid-cols-2 gap-2.5 sm:gap-3' : 'grid grid-cols-1 gap-4 sm:grid-cols-2'}>
                        <FormField control={form.control} name="nome" render={({field, fieldState}) => (
                            <FormItem>
                                {variant === 'modal' && <label htmlFor="quote-nome" className="mb-1 block text-xs font-bold sm:text-sm">Nome</label>}
                                <FormControl>
                                    <Input {...field} id="quote-nome" className={variant === 'modal' ? 'h-10 min-w-0 rounded-xl border-[#dce4ee] px-3 sm:h-11' : 'border-primary'} placeholder={variant === 'modal' ? 'Il tuo nome' : 'Nome'}/>
                                </FormControl>
                                <FormMessage>{fieldState.error?.message}</FormMessage>
                            </FormItem>
                        )}/>
                        <FormField control={form.control} name="cognome" render={({field, fieldState}) => (
                            <FormItem>
                                {variant === 'modal' && <label htmlFor="quote-cognome" className="mb-1 block text-xs font-bold sm:text-sm">Cognome</label>}
                                <FormControl>
                                    <Input {...field} id="quote-cognome" className={variant === 'modal' ? 'h-10 min-w-0 rounded-xl border-[#dce4ee] px-3 sm:h-11' : 'border-primary'} placeholder={variant === 'modal' ? 'Il tuo cognome' : 'Cognome'}/>
                                </FormControl>
                                <FormMessage>{fieldState.error?.message}</FormMessage>
                            </FormItem>
                        )}/>
                    </div>
                    <div className={variant === 'modal' ? 'grid grid-cols-2 gap-2.5 sm:gap-3' : 'grid grid-cols-1 gap-4 sm:grid-cols-2'}>
                        <FormField control={form.control} name="email" render={({field, fieldState}) => (
                            <FormItem>
                                {variant === 'modal' && <label htmlFor="quote-email" className="mb-1 block text-xs font-bold sm:text-sm">Email</label>}
                                <FormControl>
                                    <Input {...field} id="quote-email" type="email" className={variant === 'modal' ? 'h-10 min-w-0 rounded-xl border-[#dce4ee] px-3 sm:h-11' : 'border-primary'} placeholder={variant === 'modal' ? 'nome@email.it' : 'Indirizzo Email'}/>
                                </FormControl>
                                <FormMessage>{fieldState.error?.message}</FormMessage>
                            </FormItem>
                        )}/>
                        <FormField control={form.control} name="telefono" render={({field, fieldState}) => (
                            <FormItem>
                                {variant === 'modal' && <label htmlFor="quote-telefono" className="mb-1 block text-xs font-bold sm:text-sm">Telefono</label>}
                                <FormControl>
                                    <Input {...field} id="quote-telefono" type="tel" className={variant === 'modal' ? 'h-10 min-w-0 rounded-xl border-[#dce4ee] px-3 sm:h-11' : 'border-primary'} placeholder={variant === 'modal' ? '+39 333 123 4567' : 'Numero di telefono'}/>
                                </FormControl>
                                <FormMessage>{fieldState.error?.message}</FormMessage>
                            </FormItem>
                        )}/>
                    </div>
                    <FormField control={form.control} name="provincia"
                               render={({field, fieldState}) => (
                                   <FormItem>
                                       {variant === 'modal' && <label className="mb-1 block text-xs font-bold sm:text-sm">Provincia</label>}
                                       <FormControl>
                                           <Controller
                                               control={form.control}
                                               name="provincia"
                                               render={({field}) => (
                                                   <Select {...field}
                                                           onValueChange={(value) => field.onChange(value)}
                                                           value={field.value}>
                                                       <SelectTrigger
                                                           className={variant === 'modal' ? 'h-10 rounded-xl border-[#dce4ee] px-3 sm:h-11' : 'border-primary'}>
                                                           <SelectValue
                                                               placeholder="Seleziona una provincia"/>
                                                       </SelectTrigger>
                                                       <SelectContent>
                                                           {provinces.map((province) => (
                                                               <SelectItem
                                                                   key={province.code}
                                                                   value={province.code}>
                                                                   {province.name}
                                                               </SelectItem>
                                                           ))}
                                                       </SelectContent>
                                                   </Select>
                                               )}
                                           />
                                       </FormControl>
                                       <FormMessage>{fieldState.error?.message}</FormMessage>
                                   </FormItem>
                               )}/>
                    <FormField control={form.control} name="descrizione" render={({field, fieldState}) => (
                        <FormItem>
                            {variant === 'modal' && <label htmlFor="quote-descrizione" className="mb-1 block text-xs font-bold sm:text-sm">Richiesta (facoltativa)</label>}
                            <FormControl>
                                <Textarea {...field} id="quote-descrizione" className={variant === 'modal' ? 'h-[64px] min-h-[64px] rounded-xl border-[#dce4ee] px-3 py-2 sm:h-[72px]' : 'border-primary'}
                                          placeholder="Descrivi la tua richiesta"/>
                            </FormControl>
                            <FormMessage>{fieldState.error?.message}</FormMessage>
                        </FormItem>
                    )}/>
                    <div className="flex items-start gap-2.5">
                        <FormField control={form.control} name="terms" render={({field, fieldState}) => (
                            <FormItem>
                                <FormControl>
                                    <Checkbox id="terms" checked={field.value} className="text-white"
                                              onCheckedChange={field.onChange}/>
                                </FormControl>
                            </FormItem>
                        )}/>
                        <label htmlFor="terms" className={variant === 'modal' ? 'cursor-pointer text-xs leading-[1.35] text-[#4a586f] sm:text-sm' : 'cursor-pointer text-sm leading-[1.4] text-[#4a586f]'}>
                            Accetto i <a href="https://www.iubenda.com/termini-e-condizioni/31103949" target="_blank" rel="noopener noreferrer" className="text-primary underline">termini e condizioni</a> e <a href="https://www.iubenda.com/privacy-policy/31103949" target="_blank" rel="noopener noreferrer" className="text-primary underline">l’informativa privacy</a>.
                        </label>
                        {form.formState.errors.terms && (
                            <div className="text-red-500 text-sm mt-1">
                                {form.formState.errors.terms.message}
                            </div>
                        )}
                    </div>
                    {submitError && <p role="alert" className="text-sm text-red-700">{submitError}</p>}
                    <button type="submit" disabled={form.formState.isSubmitting}
                        className={variant === 'modal' ? 'w-full rounded-[14px] bg-[#d20000] px-6 py-3 text-center text-base font-extrabold text-white disabled:opacity-60 sm:py-3.5' : 'bg-gradient-custom px-6 py-2 min-w-[200px] rounded-lg text-white text-center font-bold'}>{form.formState.isSubmitting ? 'Invio in corso…' : variant === 'modal' ? 'Scarica preventivo' : 'Invia'}
                    </button>
                </form>
            </Form>
        </section>
    )
});

export default FromNoleggio;
